import React, { useEffect, useMemo, useState } from "react";
import { Add, Block, Edit, Lock, Save, Visibility } from "@mui/icons-material";
import {
  assignRecoveryAgent,
  closeInvestment,
  createInvestment,
  disburseInvestment,
  fetchClientFiche,
  fetchClients,
  fetchInvestments,
  fetchProducts,
  fetchUsers,
  updateInvestment,
  updateInvestmentStatus,
} from "../services/owdedApi";
import { BASE_URL } from "../services/constant";
import { useAuth } from "../context/AuthContext";
import { dateOnly, money, percent } from "./format";
import { EmptyState, LoadingOverlay, Modal, Pagination, StatusPill } from "./ui";
import { paginateRows, PAGE_SIZE } from "./listUtils";
import { hasSuperAccess } from "../utils/access";

const initialForm = {
  client_id: "",
  product_id: "",
  subscription_amount: 300000,
  agent_recovery_id: "",
  repayment_start_date: "",
  analysis_notes: "",
};

function contributionInstallments(product) {
  if (!product) return 1;
  const duration = Number(product.duration_value || 1);
  const frequency = product.contribution_frequency || product.repayment_modality || "DAILY";
  if (frequency === "MONTHLY") return Math.max(duration, 1);
  if (frequency === "WEEKLY") {
    const totalDays = product.duration_unit === "MONTH" ? duration * 30 : duration;
    return Math.max(Math.ceil(totalDays / 7), 1);
  }
  if (product.duration_unit === "MONTH") return Math.max(duration * 30, 1);
  return Math.max(duration, 1);
}

function isBootstrapping(product) {
  return String(product?.product_family || product?.product_type_label || "").toUpperCase() === "BOOTSTRAPPING";
}

function investmentPreview(product, amount) {
  const gross = Number(amount || 0);
  if (!product) {
    return { commission: 0, net: gross, amountToRecover: gross, contribution: 0, monthlyCommission: 0 };
  }

  const rate = Number(product.commission_rate || 0);
  const installments = contributionInstallments(product);
  const monthlyCommission = Number(((gross * rate) / 100).toFixed(2));
  const commission = isBootstrapping(product) ? Number((monthlyCommission * installments).toFixed(2)) : monthlyCommission;
  const net = isBootstrapping(product) ? gross : Number((gross - commission).toFixed(2));
  const amountToRecover = isBootstrapping(product) ? Number((gross + commission).toFixed(2)) : gross;
  const contribution = Number((amountToRecover / Math.max(installments, 1)).toFixed(2));

  return { commission, net, amountToRecover, contribution, monthlyCommission };
}

function InvestmentsPage() {
  const { user } = useAuth();
  const [investments, setInvestments] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [assignAgentId, setAssignAgentId] = useState("");
  const [clientFiche, setClientFiche] = useState(null);
  const [clientFicheOpen, setClientFicheOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [formError, setFormError] = useState("");

  const load = async () => {
    const [investmentRows, clientRows, productRows, userRows] = await Promise.all([
      fetchInvestments(),
      fetchClients(),
      fetchProducts({ status: "ACTIVE" }),
      fetchUsers().catch(() => []),
    ]);
    setInvestments(investmentRows);
    setClients(clientRows);
    setProducts(productRows);
    setUsers(userRows);
  };

  useEffect(() => {
    load().catch(() => setNotice("Impossible de charger les investissements."));
  }, []);

  const recoveryAgents = useMemo(
    () => users.filter((user) => user.role_code === "AGENT_RECOUVREMENT"),
    [users]
  );

  const statusOptions = useMemo(() => [
    { key: "ALL", label: "Tous", statuses: [] },
    { key: "DEMANDE", label: "En demande", statuses: ["DEMANDE", "ANALYSE", "VALIDATION"] },
    { key: "VALIDE", label: "VALIDE", statuses: ["VALIDE"] },
    { key: "DECAISSE", label: "DECAISSE", statuses: ["DECAISSE"] },
    { key: "EN_RECOUVREMENT", label: "EN RECOUVREMENT", statuses: ["EN_RECOUVREMENT"] },
    { key: "CLOTURE", label: "CLOTURE", statuses: ["CLOTURE"] },
  ], []);

  const statusCounts = useMemo(() => statusOptions.reduce((counts, option) => ({
    ...counts,
    [option.key]: option.key === "ALL"
      ? investments.length
      : investments.filter((investment) => option.statuses.includes(investment.status)).length,
  }), {}), [investments, statusOptions]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    const option = statusOptions.find((item) => item.key === statusFilter);
    return investments.filter((investment) => {
      const matchesStatus = !option || option.key === "ALL" || option.statuses.includes(investment.status);
      const matchesSearch = [investment.reference, investment.client_name, investment.client_number, investment.product_name, investment.status]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
      return matchesStatus && matchesSearch;
    });
  }, [investments, search, statusFilter, statusOptions]);

  const pagination = useMemo(() => paginateRows(filtered, page, PAGE_SIZE), [filtered, page]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, investments.length]);

  const selectedProduct = products.find((product) => String(product.id) === String(form.product_id));
  const gross = Number(form.subscription_amount || 0);
  const minAmount = Number(selectedProduct?.min_amount || 0);
  const maxAmount = Number(selectedProduct?.max_amount || 0);
  const amountOutOfRange = selectedProduct && (gross < minAmount || gross > maxAmount);
  const preview = investmentPreview(selectedProduct, gross);
  const commission = preview.commission;
  const net = preview.net;
  const contribution = preview.contribution;
  const canCloseInvestment = hasSuperAccess(user);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setFormError("");
    setModalOpen(true);
  };

  const openEdit = (investment) => {
    setEditing(investment);
    setForm({
      client_id: investment.client_id || "",
      product_id: investment.product_id || "",
      subscription_amount: investment.subscription_amount || investment.amount_invested || 0,
      agent_recovery_id: investment.agent_recovery_id || "",
      repayment_start_date: investment.repayment_start_date ? String(investment.repayment_start_date).slice(0, 10) : "",
      analysis_notes: investment.analysis_notes || "",
    });
    setFormError("");
    setModalOpen(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!selectedProduct) {
      setFormError("Veuillez selectionner un produit.");
      return;
    }

    if (amountOutOfRange) {
      setFormError(`Le montant doit etre compris entre ${money(minAmount, selectedProduct.currency)} et ${money(maxAmount, selectedProduct.currency)}.`);
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...form,
        amount_invested: form.subscription_amount,
        agent_recovery_id: form.agent_recovery_id || null,
      };

      if (editing) {
        await updateInvestment(editing.id, payload);
      } else {
        await createInvestment(payload);
      }
      setForm(initialForm);
      setEditing(null);
      setModalOpen(false);
      setNotice(editing ? "Souscription modifiee." : "Souscription creee avec ID de carte de cotisation.");
      await load();
    } catch (err) {
      setFormError(err.response?.data?.error || "Impossible d'enregistrer la souscription.");
    } finally {
      setLoading(false);
    }
  };

  const validate = async (id) => {
    setLoading(true);
    try {
      await updateInvestmentStatus(id, { status: "VALIDE" });
      setNotice("Souscription validee.");
      await load();
    } finally {
      setLoading(false);
    }
  };

  const reject = async (investment) => {
    const reason = window.prompt(`Motif de rejet pour ${investment.reference} :`);
    if (reason === null) return;
    setLoading(true);
    try {
      await updateInvestmentStatus(investment.id, { status: "ANNULE", validation_notes: reason || "Non conforme" });
      setNotice("Souscription rejetee.");
      await load();
    } finally {
      setLoading(false);
    }
  };

  const disburse = async (id) => {
    const investment = investments.find((item) => Number(item.id) === Number(id));
    const amount = investment ? money(investment.net_disbursement_amount, investment.currency || "CDF") : "le montant net";
    if (!window.confirm(`Confirmez-vous le decaissement de ${amount} pour ce dossier ?`)) return;
    setLoading(true);
    try {
      await disburseInvestment(id, {});
      setNotice("Montant net decaisse et echeancier genere.");
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Decaissement impossible. Verifiez le solde du compte caisse.");
    } finally {
      setLoading(false);
    }
  };

  const close = async (investment) => {
    const remaining = Number(investment.remaining_amount || 0);
    const message = remaining > 0
      ? `Ce dossier garde un solde de ${money(remaining, investment.currency || "CDF")}. Confirmez-vous la cloture DG ?`
      : `Confirmez-vous la cloture du dossier ${investment.collection_card_id || investment.reference} ?`;
    if (!window.confirm(message)) return;

    const reason = window.prompt("Motif de cloture :", remaining > 0 ? "Cloture DG avec solde restant" : "Cloture DG");
    if (reason === null) return;

    setLoading(true);
    try {
      await closeInvestment(investment.id, { force: true, reason: reason || "Cloture DG" });
      setNotice("Investissement cloture.");
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Cloture impossible.");
    } finally {
      setLoading(false);
    }
  };

  const assign = async (id, agentId) => {
    if (!agentId) return;
    setLoading(true);
    try {
      await assignRecoveryAgent(id, { agent_recovery_id: agentId });
      setNotice("Agent affecte.");
      setAssigning(null);
      setAssignAgentId("");
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Impossible d'affecter l'agent.");
    } finally {
      setLoading(false);
    }
  };

  const openAssign = (investment) => {
    setAssigning(investment);
    setAssignAgentId(investment.agent_recovery_id || "");
  };

  const assetUrl = (url) => {
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE_URL}${url}`;
  };

  const selectedAssignAgent = recoveryAgents.find((agent) => String(agent.id) === String(assignAgentId));

  const openClientFiche = async (investment) => {
    setClientFicheOpen(true);
    setClientFiche(null);
    setLoading(true);
    try {
      setClientFiche(await fetchClientFiche(investment.client_id));
    } catch (err) {
      setNotice("Impossible de charger la fiche client.");
      setClientFicheOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="page-content management-page">
      <div className="page-head">
        <div>
          <h1>Souscriptions et investissements</h1>
          <p>Produit souscrit, montant brut, commission, montant net et cotisation attendue.</p>
        </div>
      </div>
      {notice && <p className="notice">{notice}</p>}

      <section className="panel data-panel">
        <div className="table-toolbar">
          <input placeholder="Recherche avancee..." value={search} onChange={(event) => setSearch(event.target.value)} />
          <div className="status-filter">
            {statusOptions.map((option) => (
              <button
                className={statusFilter === option.key ? "active" : ""}
                type="button"
                key={option.key}
                onClick={() => setStatusFilter(option.key)}
              >
                {option.label} <strong>{statusCounts[option.key] || 0}</strong>
              </button>
            ))}
          </div>
          <button className="primary-btn" type="button" onClick={openCreate}><Add /> Ajouter</button>
        </div>
        {filtered.length ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID carte</th>
                <th>Client</th>
                <th>Produit</th>
                <th>Brut</th>
                <th>Net client</th>
                <th>Cotisation</th>
                <th>Recouvrement</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagination.rows.map((investment) => (
                <tr key={investment.id}>
                  <td>{investment.collection_card_id || investment.reference}</td>
                  <td>{investment.client_name}<br /><small>{investment.client_number}</small></td>
                  <td>{investment.product_name}</td>
                  <td>{money(investment.subscription_amount || investment.amount_invested, investment.currency || "CDF")}</td>
                  <td>{money(investment.net_disbursement_amount, investment.currency || "CDF")}</td>
                  <td>{money(investment.expected_contribution_amount, investment.currency || "CDF")}</td>
                  <td>{money(investment.amount_recovered, investment.currency || "CDF")}<br /><small>{percent(investment.recovery_rate)}</small></td>
                  <td><StatusPill value={investment.status} /></td>
                  <td>
                    <div className="table-actions">
                      {["DEMANDE", "ANALYSE", "VALIDATION"].includes(investment.status) && <button className="icon-action" type="button" onClick={() => openEdit(investment)} aria-label="Modifier"><Edit /></button>}
                      <button className="icon-action" type="button" onClick={() => openClientFiche(investment)} aria-label="Voir fiche client"><Visibility /></button>
                      {["DEMANDE", "ANALYSE", "VALIDATION"].includes(investment.status) && <button className="ghost-btn" type="button" onClick={() => validate(investment.id)}>Valider</button>}
                      {["DEMANDE", "ANALYSE", "VALIDATION"].includes(investment.status) && <button className="icon-action danger" type="button" onClick={() => reject(investment)} aria-label="Rejeter"><Block /></button>}
                      {investment.status === "VALIDE" && <button className="primary-btn" type="button" onClick={() => disburse(investment.id)}>Decaisser</button>}
                      {["VALIDE", "DECAISSE", "EN_RECOUVREMENT"].includes(investment.status) && <button className="ghost-btn" type="button" onClick={() => openAssign(investment)}>Affecter</button>}
                      {canCloseInvestment && investment.status !== "CLOTURE" && <button className="ghost-btn" type="button" onClick={() => close(investment)}><Lock /> Cloturer</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <EmptyState />}
        <Pagination
          page={pagination.safePage}
          totalPages={pagination.totalPages}
          totalItems={filtered.length}
          from={pagination.from}
          to={pagination.to}
          onPageChange={setPage}
        />
      </section>

      {modalOpen && (
        <Modal
          title={editing ? "Modifier la souscription" : "Ajouter une souscription"}
          onClose={() => { setModalOpen(false); setEditing(null); }}
          footer={<button className="primary-btn" form="investment-form"><Save /> Enregistrer</button>}
        >
          <form id="investment-form" onSubmit={submit}>
            {formError && <p className="error-text">{formError}</p>}
            <div className="form-grid">
              <label>Client<select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} required><option value="">Selectionner</option>{clients.map((client) => <option value={client.id} key={client.id}>{client.generated_client_id || client.client_number} - {client.full_name}</option>)}</select></label>
              <label>Produit<select value={form.product_id} onChange={(e) => {
                const product = products.find((item) => String(item.id) === String(e.target.value));
                const currentAmount = Number(form.subscription_amount || 0);
                let nextAmount = currentAmount;
                if (product) {
                  const min = Number(product.min_amount || 0);
                  const max = Number(product.max_amount || 0);
                  if (!currentAmount || currentAmount < min) nextAmount = min;
                  if (max && currentAmount > max) nextAmount = max;
                }
                setForm({ ...form, product_id: e.target.value, subscription_amount: nextAmount });
                setFormError("");
              }} required><option value="">Selectionner</option>{products.map((product) => <option value={product.id} key={product.id}>{product.name} ({money(product.min_amount, product.currency)} - {money(product.max_amount, product.currency)})</option>)}</select></label>
              <label>Montant souscrit<input type="number" min={minAmount || 0} max={maxAmount || undefined} value={form.subscription_amount} onChange={(e) => { setForm({ ...form, subscription_amount: e.target.value }); setFormError(""); }} required />{selectedProduct && <span className="field-hint">Min {money(minAmount, selectedProduct.currency)} / Max {money(maxAmount, selectedProduct.currency)}</span>}</label>
              <label>Agent de credit<select value={form.agent_recovery_id} onChange={(e) => setForm({ ...form, agent_recovery_id: e.target.value })}><option value="">Agent connecte</option>{recoveryAgents.map((agent) => <option value={agent.id} key={agent.id}>{agent.full_name}</option>)}</select></label>
              <label>Date debut cotisation<input type="date" value={form.repayment_start_date} onChange={(e) => setForm({ ...form, repayment_start_date: e.target.value })} /></label>
              <label>Montant net client<input value={money(net, selectedProduct?.currency || "CDF")} readOnly /></label>
              <label>Commission<input value={money(commission, selectedProduct?.currency || "CDF")} readOnly /></label>
              <label>Cotisation attendue<input value={money(contribution, selectedProduct?.currency || "CDF")} readOnly /></label>
              <label>Total a rembourser<input value={money(preview.amountToRecover, selectedProduct?.currency || "CDF")} readOnly /></label>
              {selectedProduct && isBootstrapping(selectedProduct) && <div className="field-note full-row">Bootstrapping : commission mensuelle {money(preview.monthlyCommission, selectedProduct.currency)} x {contributionInstallments(selectedProduct)} mois.</div>}
              <label className="full-row">Observation<textarea value={form.analysis_notes} onChange={(e) => setForm({ ...form, analysis_notes: e.target.value })} /></label>
            </div>
          </form>
        </Modal>
      )}
      {assigning && (
        <Modal
          title={`Affecter le recouvrement - ${assigning.collection_card_id || assigning.reference}`}
          onClose={() => { setAssigning(null); setAssignAgentId(""); }}
          footer={<button className="primary-btn" type="button" onClick={() => assign(assigning.id, assignAgentId)} disabled={!assignAgentId}><Save /> Affecter</button>}
        >
          <div className="assignment-modal-content">
            <section className="assignment-dossier-card">
              <div>
                <span>Dossier</span>
                <strong>{assigning.collection_card_id || assigning.reference}</strong>
                <small>{assigning.status}</small>
              </div>
              <dl>
                <dt>Client</dt><dd>{assigning.client_name}</dd>
                <dt>Produit</dt><dd>{assigning.product_name}</dd>
                <dt>Montant a recouvrer</dt><dd>{money(assigning.amount_to_recover, assigning.currency || "CDF")}</dd>
                <dt>Cotisation</dt><dd>{money(assigning.expected_contribution_amount, assigning.currency || "CDF")}</dd>
                <dt>Agent actuel</dt><dd>{assigning.recovery_agent_name || "Non affecte"}</dd>
              </dl>
            </section>
            <label className="assignment-select">Agent de recouvrement
              <select value={assignAgentId} onChange={(event) => setAssignAgentId(event.target.value)} required>
                <option value="">Selectionner un recouvreur</option>
                {recoveryAgents.map((agent) => <option value={agent.id} key={agent.id}>{agent.full_name} - {agent.role_label || agent.role_code}</option>)}
              </select>
            </label>
            <p className="field-note">L'affectation donne acces au dossier de recouvrement sans effectuer le decaissement.</p>
            {selectedAssignAgent && (
              <section className="assignment-agent-card">
                <div className="assignment-agent-card__avatar">
                  {selectedAssignAgent.photo_url ? <img src={assetUrl(selectedAssignAgent.photo_url)} alt={selectedAssignAgent.full_name} /> : <span>{String(selectedAssignAgent.full_name || "?").slice(0, 1)}</span>}
                </div>
                <div>
                  <span>Recouvreur selectionne</span>
                  <strong>{selectedAssignAgent.full_name}</strong>
                  <small>{selectedAssignAgent.role_label || selectedAssignAgent.role_code}</small>
                  <small>{selectedAssignAgent.phone || selectedAssignAgent.email || "-"}</small>
                </div>
              </section>
            )}
          </div>
        </Modal>
      )}
      {clientFicheOpen && (
        <Modal
          title={clientFiche?.client ? `Fiche client - ${clientFiche.client.full_name}` : "Fiche client"}
          onClose={() => { setClientFicheOpen(false); setClientFiche(null); }}
        >
          {!clientFiche?.client ? (
            <EmptyState label="Chargement de la fiche..." />
          ) : (
            <div className="client-fiche compact-fiche">
              <div className="fiche-head">
                <div>
                  <span>Client</span>
                  <h2>{clientFiche.client.full_name}</h2>
                  <p>{clientFiche.client.generated_client_id || clientFiche.client.client_number}</p>
                </div>
                {clientFiche.client.photo_client_url && <img src={assetUrl(clientFiche.client.photo_client_url)} alt="Client" />}
              </div>
              <div className="fiche-grid">
                <section>
                  <h3>Identification</h3>
                  <dl>
                    <dt>Telephone</dt><dd>{clientFiche.client.phone || "-"}</dd>
                    <dt>Adresse</dt><dd>{clientFiche.client.address || "-"} {clientFiche.client.commune || ""}</dd>
                    <dt>Agent</dt><dd>{clientFiche.client.commercial_agent_name || "-"}</dd>
                    <dt>Score risque</dt><dd>{clientFiche.client.risk_score || 0}</dd>
                  </dl>
                </section>
                <section>
                  <h3>Activites</h3>
                  {clientFiche.activities?.length ? <div className="fiche-tags">{clientFiche.activities.map((activity) => <span key={activity.id}>{activity.activity_name}</span>)}</div> : <p>Aucune activite.</p>}
                </section>
              </div>
              <section className="fiche-section">
                <h3>Historique investissements</h3>
                {clientFiche.investments?.length ? (
                  <table className="fiche-table compact">
                    <tbody>{clientFiche.investments.map((item) => (
                      <tr key={item.id}>
                        <td>{item.product_name}</td>
                        <td>{money(item.amount_invested, item.currency || "CDF")}</td>
                        <td>Cot. {money(item.expected_contribution_amount, item.currency || "CDF")}</td>
                        <td><StatusPill value={item.status} /></td>
                      </tr>
                    ))}</tbody>
                  </table>
                ) : <p>Aucun investissement.</p>}
              </section>
              <section className="fiche-section">
                <h3>Recouvrements</h3>
                {clientFiche.recoveries?.length ? (
                  <table className="fiche-table compact">
                    <tbody>{clientFiche.recoveries.slice(0, 10).map((item) => (
                      <tr key={item.id}>
                        <td>{dateOnly(item.paid_at)}</td>
                        <td>{money(item.amount_paid, item.currency || "CDF")}</td>
                        <td>{item.recovery_agent_name || "-"}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                ) : <p>Aucun recouvrement.</p>}
              </section>
            </div>
          )}
        </Modal>
      )}
      <LoadingOverlay show={loading} label="Affectation en cours..." />
    </main>
  );
}

export default InvestmentsPage;
